/**
 * SkillSidebar — Panel showing available skills, categorized and searchable.
 * Click → inserts slash command into chat input via runai:chat-insert event.
 *
 * Rule 94: Modüler — tek sorumluluk.
 * CSS: var(--os-*) only. Icons: lucide-react only. Max 250 lines.
 */
import {
    BookOpen, Code2, Image, Languages, Lightbulb,
    Music, Search, Sparkles, Volume2, X, Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { APP_REGISTRY, type AppDefinition } from '../../../lib/appRegistry';

/* ── Category config ── */
const CATEGORY_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  All:          { label: 'All',        icon: <Sparkles size={11} />, color: 'var(--os-accent)' },
  Creative:     { label: 'Creative',   icon: <Image size={11} />,    color: '#a855f7' },
  Productivity: { label: 'Productivity', icon: <Lightbulb size={11} />, color: '#f59e0b' },
  Research:     { label: 'Research',   icon: <BookOpen size={11} />, color: '#3b82f6' },
  Code:         { label: 'Code',       icon: <Code2 size={11} />,    color: '#10b981' },
  Media:        { label: 'Media',      icon: <Music size={11} />,    color: '#ec4899' },
  AI:           { label: 'AI',         icon: <Zap size={11} />,      color: '#6366f1' },
}

/* Map skill/extension slash commands to display categories */
function getDisplayCategory(app: AppDefinition): string {
  if (app.category === 'AI') return 'AI'
  if (['Creative'].includes(app.category)) return 'Creative'
  if (['Productivity'].includes(app.category)) return 'Productivity'
  if (['Development'].includes(app.category)) return 'Code'
  return app.category
}

/* Icon map for skill icons (lucide names → components) */
const ICON_MAP: Record<string, React.ReactNode> = {
  Image:     <Image size={14} />,
  Languages: <Languages size={14} />,
  Volume2:   <Volume2 size={14} />,
  FileText:  <BookOpen size={14} />,
  Sparkles:  <Sparkles size={14} />,
  Code2:     <Code2 size={14} />,
  Music:     <Music size={14} />,
  Zap:       <Zap size={14} />,
}

function SkillIcon({ iconName }: { iconName: string }) {
  return <>{ICON_MAP[iconName] ?? <Zap size={14} />}</>
}

interface Props {
  onClose?: () => void
}

export function SkillSidebar({ onClose }: Props) {
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState('All')

  // Collect all skills + extensions with slash commands
  const allSkills = useMemo(() =>
    APP_REGISTRY.filter(a =>
      (a.type === 'skill' || a.type === 'extension') && a.slashCommand
    ),
    []
  )

  const categories = useMemo(() => {
    const cats = new Set(['All'])
    allSkills.forEach(a => cats.add(getDisplayCategory(a)))
    return Array.from(cats)
  }, [allSkills])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return allSkills.filter(a => {
      const matchesSearch = !q ||
        a.name.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        (a.slashCommand ?? '').toLowerCase().includes(q)
      const matchesCat = activeCategory === 'All' || getDisplayCategory(a) === activeCategory
      return matchesSearch && matchesCat
    })
  }, [allSkills, search, activeCategory])

  const insertCommand = (cmd: string) => {
    window.dispatchEvent(new CustomEvent('runai:chat-insert', { detail: cmd + ' ' }))
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: 'var(--os-bg)', color: 'var(--os-text)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '10px 12px', borderBottom: '1px solid var(--os-border)',
        flexShrink: 0,
      }}>
        <Zap size={14} style={{ color: 'var(--os-accent)' }} />
        <span style={{ fontWeight: 700, fontSize: 12, flex: 1 }}>Skills</span>
        <span style={{ fontSize: 9.5, color: 'var(--os-muted)' }}>{filtered.length} available</span>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--os-muted)', padding: 2, borderRadius: 4,
            display: 'flex', alignItems: 'center',
          }}>
            <X size={13} />
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ padding: '8px 12px', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--os-surface)', border: '1px solid var(--os-border)',
          borderRadius: 7, padding: '5px 8px',
        }}>
          <Search size={11} style={{ color: 'var(--os-muted)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search skills..."
            style={{
              background: 'none', border: 'none', outline: 'none',
              color: 'var(--os-text)', fontSize: 11, flex: 1,
              fontFamily: 'inherit',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--os-muted)', padding: 0, display: 'flex',
            }}>
              <X size={10} />
            </button>
          )}
        </div>
      </div>

      {/* Category tabs */}
      <div style={{
        display: 'flex', gap: 4, padding: '0 12px 8px',
        overflowX: 'auto', flexShrink: 0,
        scrollbarWidth: 'none',
      }}>
        {categories.map(cat => {
          const meta = CATEGORY_META[cat]
          const isActive = activeCategory === cat
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', borderRadius: 20, flexShrink: 0,
                background: isActive ? `${meta?.color ?? 'var(--os-accent)'}20` : 'var(--os-surface)',
                border: `1px solid ${isActive ? (meta?.color ?? 'var(--os-accent)') : 'var(--os-border)'}`,
                color: isActive ? (meta?.color ?? 'var(--os-accent)') : 'var(--os-muted)',
                cursor: 'pointer', fontSize: 10, fontWeight: isActive ? 600 : 400,
                fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}
            >
              {meta?.icon}
              {meta?.label ?? cat}
            </button>
          )
        })}
      </div>

      {/* Skill list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
        {filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '24px 0',
            color: 'var(--os-muted)', fontSize: 11,
          }}>
            No skills found
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filtered.map(skill => (
              <SkillItem key={skill.id} skill={skill} onInsert={insertCommand} />
            ))}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div style={{
        padding: '6px 12px', borderTop: '1px solid var(--os-border)',
        fontSize: 9.5, color: 'var(--os-muted)', textAlign: 'center', flexShrink: 0,
      }}>
        Click to insert command into chat
      </div>
    </div>
  )
}

/* ── Skill Item ── */
function SkillItem({ skill, onInsert }: { skill: AppDefinition; onInsert: (cmd: string) => void }) {
  const [hovered, setHovered] = useState(false)
  const cat = getDisplayCategory(skill)
  const catColor = CATEGORY_META[cat]?.color ?? 'var(--os-accent)'

  return (
    <button
      onClick={() => onInsert(skill.slashCommand!)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 8px', borderRadius: 8, width: '100%', textAlign: 'left',
        background: hovered ? 'var(--os-hover)' : 'var(--os-surface)',
        border: `1px solid ${hovered ? 'var(--os-accent)' : 'var(--os-border)'}`,
        cursor: 'pointer', fontFamily: 'inherit',
        transition: 'background 0.1s, border-color 0.1s',
      }}
    >
      {/* Icon */}
      <div style={{
        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
        background: `${catColor}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: catColor,
      }}>
        <SkillIcon iconName={skill.icon} />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontWeight: 600, fontSize: 11, color: 'var(--os-text)' }}>{skill.name}</span>
          <span style={{
            fontSize: 9, color: catColor, background: `${catColor}15`,
            padding: '1px 5px', borderRadius: 8, fontWeight: 600,
          }}>
            {skill.slashCommand}
          </span>
        </div>
        <div style={{
          fontSize: 10, color: 'var(--os-muted)', marginTop: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {skill.description}
        </div>
      </div>

      {/* Arrow hint on hover */}
      {hovered && (
        <span style={{ fontSize: 10, color: 'var(--os-accent)', flexShrink: 0 }}>↵</span>
      )}
    </button>
  )
}

export default SkillSidebar
